import { useState, useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Plus, Trash2, FileText, Search, AlertTriangle } from "lucide-react";
import "./Anotacoes.css";

export default function Anotacoes() {
    const [notes, setNotes] = useState(() => {
        const saved = localStorage.getItem("myWorkspaceNotes");
        try {
            const parsed = JSON.parse(saved);
            if (!parsed) return [];
            return Array.isArray(parsed) ? parsed.map(n => ({
                id: n.id || Date.now(),
                title: n.title || "Sem título",
                content: n.content || "",
                date: n.lastModified ? new Date(n.lastModified) : new Date()
            })) : [];
        } catch (e) { return []; }
    });

    const [activeNoteId, setActiveNoteId] = useState(notes[0]?.id || null);
    const [searchTerm, setSearchTerm] = useState("");
    const [noteToDelete, setNoteToDelete] = useState(null);

    useEffect(() => {
        localStorage.setItem("myWorkspaceNotes", JSON.stringify(notes));
    }, [notes]);

    const handleAddNote = () => {
        const newNote = {
            id: Date.now(),
            title: "Nova Anotação",
            content: "",
            date: new Date()
        };
        setNotes([newNote, ...notes]);
        setActiveNoteId(newNote.id);
    };

    const requestDelete = (e, id) => {
        e.stopPropagation();
        setNoteToDelete(id);
    };

    const confirmDelete = () => {
        if (noteToDelete) {
            const newNotes = notes.filter(n => n.id !== noteToDelete);
            setNotes(newNotes);

            if (activeNoteId === noteToDelete) {
                setActiveNoteId(newNotes[0]?.id || null);
            }
            setNoteToDelete(null);
        }
    };

    const handleUpdateNote = (key, value) => {
        const updatedNotes = notes.map((note) => {
            if (note.id === activeNoteId) {
                return { ...note, [key]: value, date: new Date() };
            }
            return note;
        });
        setNotes(updatedNotes);
    };

    // Fallback para a primeira nota se o ID ativo não existir mais
    const activeNote = notes.find(note => note.id === activeNoteId) || notes[0] || null;

    const filteredNotes = notes.filter(note =>
        (note.title && note.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (note.content && note.content.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'clean']
        ],
    };

    return (
        <div className="notes-container">

            {/* SIDEBAR */}
            <aside className="notes-sidebar">
                <div className="notes-header">
                    <h2><FileText size={24} /> Notas</h2>
                    <button onClick={handleAddNote} className="btn-add-note">
                        <Plus size={20} />
                    </button>
                </div>

                <div className="search-box">
                    <Search size={16} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Buscar notas..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="notes-list">
                    {filteredNotes.map(note => (
                        <div
                            key={note.id}
                            className={`note-item ${note.id === activeNoteId ? "active" : ""}`}
                            onClick={() => setActiveNoteId(note.id)}
                        >
                            <div className="note-info">
                                <strong>{note.title || "Sem título"}</strong>
                                <span className="note-preview">
                                    {note.content
                                        ? note.content.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').slice(0, 40)
                                        : "Sem conteúdo..."}
                                </span>
                            </div>
                            <button
                                className="btn-delete-note"
                                onClick={(e) => requestDelete(e, note.id)}
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                    {filteredNotes.length === 0 && <p className="empty-msg">Nenhuma nota encontrada.</p>}
                </div>
            </aside>

            {/* EDITOR */}
            <main className="notes-editor-area">
                {!activeNote ? (
                    <div className="no-active-note">Selecione ou crie uma nota para começar.</div>
                ) : (
                    <div className="editor-wrapper">
                        <input
                            type="text"
                            className="note-title-input"
                            value={activeNote.title}
                            onChange={(e) => handleUpdateNote("title", e.target.value)}
                            placeholder="Título da Anotação"
                        />
                        <div className="quill-container">
                            <ReactQuill
                                key={activeNote.id}
                                theme="snow"
                                value={activeNote.content}
                                onChange={(content) => handleUpdateNote("content", content)}
                                modules={modules}
                                placeholder="Escreva algo incrível..."
                            />
                        </div>
                    </div>
                )}
            </main>

            {/* MODAL DE CONFIRMAÇÃO */}
            {noteToDelete && (
                <div className="notes-modal-overlay" onClick={() => setNoteToDelete(null)}>
                    <div className="notes-modal-box" onClick={e => e.stopPropagation()}>
                        <div className="notes-modal-icon-area">
                            <AlertTriangle size={48} color="#ef4444" />
                        </div>
                        <h3>Excluir Anotação?</h3>
                        <p>Você tem certeza que deseja excluir esta nota permanentemente?</p>

                        <div className="notes-modal-actions">
                            <button
                                className="notes-btn-cancel"
                                onClick={() => setNoteToDelete(null)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="notes-btn-confirm-delete"
                                onClick={confirmDelete}
                            >
                                Sim, Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}